import { useMutation, useQueryClient, queryOptions, type QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { client } from '@blazeteams/sdk/client';
import { useAuthStore } from '@/store/auth-store';
import { sessionScope, sessionTenantId, sessionRevision, requireCurrentSession } from '@/features/auth/session-scope';
import { getLocaleSnapshot } from '@/lib/i18n/runtime';
import { LocalizedError } from '@/lib/i18n/errors';
import { usePresenceStore } from '@/lib/presence';
import { ChatCreationError, chatCreationProblem, readChatCreation, readChatPage, readChatDirectory, snapshotChatInput } from './creation-contract';
import type { Channel, CreateChannelInput } from './queries';

export function captureChatOwner() {
  const state=useAuthStore.getState();
  return {user:state.user?.id, tenant:sessionTenantId(state), scope:sessionScope(state), revision:sessionRevision(), language:getLocaleSnapshot().locale};
}
type Owner=ReturnType<typeof captureChatOwner>;
export function currentChatChannelsKey() {
  const owner=captureChatOwner();
  return ['team-chat','channels','owner',owner.scope,owner.revision] as const;
}
export function currentChatChannel(qc:QueryClient,id:string):Channel|undefined {
  const owner=captureChatOwner();
  for(const [,rows] of qc.getQueriesData<Channel[]>({queryKey:['team-chat','channels','owner',owner.scope,owner.revision]})) {
    const found=rows?.find(row=>row.id===id&&row.tenant_id===owner.tenant&&row.members?.some(member=>member.user_id===owner.user));
    if(found)return found;
  }
}
function current(owner:Owner,signal?:AbortSignal) {requireCurrentSession(owner.revision);if(signal?.aborted)throw new DOMException('Aborted','AbortError');}
export const ownedChatQueries={
  channels:(includeArchived=false)=>{
    const owner=captureChatOwner();
    return queryOptions({queryKey:['team-chat','channels','owner',owner.scope,owner.revision,{archived:includeArchived}],enabled:owner.scope!=='anonymous',retry:false,queryFn:async({signal})=>{
      current(owner,signal);let response;
      try {response=await client.get<unknown>({url:'/channels',signal,query:includeArchived?{include_archived:true}:undefined,headers:{'Accept-Language':owner.language}});} catch {current(owner,signal);throw new LocalizedError('common','comms.channelsFailed');}
      current(owner,signal);
      if(response.error||response.response?.status!==200)throw new LocalizedError('common','comms.channelsFailed');
      try {return readChatPage(response.data,owner);}catch {throw new LocalizedError('common','comms.channelsFailed');}
    }});
  },
  directory:()=>{
    const owner=captureChatOwner();
    return queryOptions({queryKey:['users','directory','owner',owner.scope,owner.revision],enabled:owner.scope!=='anonymous',retry:false,queryFn:async({signal})=>{
      current(owner,signal);let response;
      try {response=await client.get<unknown>({url:'/users/directory',signal,headers:{'Accept-Language':owner.language}});}catch {current(owner,signal);throw new LocalizedError('common','comms.directoryFailed');}
      current(owner,signal);if(response.error||response.response?.status!==200)throw new LocalizedError('common','comms.directoryFailed');
      const result=readChatDirectory(response.data);
      current(owner,signal);
      usePresenceStore.getState().hydrate(result.departments.flatMap(group=>group.users).map(user=>({id:user.id,presence:user.presence,lastSeen:user.lastSeen})));
      return result;
    }});
  },
};

type Action={owner:Owner;id:number;phase:'pending'|'uncertain'|'reviewing'|'reviewed';review?:Channel[];failed?:boolean};
let sequence=0;
export const useChatCreationState=create<{action:Action|null}>(()=>({action:null}));
useAuthStore.subscribe((next,previous)=>{if(sessionScope(next)!==sessionScope(previous))useChatCreationState.setState({action:null});});
function ownAction(owner:Owner) {const action=useChatCreationState.getState().action;return action?.owner.scope===owner.scope&&action.owner.revision===owner.revision?action:null;}
function lease(action:Action) {current(action.owner);if(ownAction(action.owner)?.id!==action.id)throw new DOMException('Aborted','AbortError');}
type Intent=Action&{body:CreateChannelInput};
const credentials=new WeakMap<object,string>();
function authorize(intent:object) {const token=useAuthStore.getState().token;if(!token)throw new ChatCreationError('unauthorized',false);credentials.set(intent,token.startsWith('Bearer ')?token:'Bearer '+token);}

export function useManagedChatCreation() {
  const qc=useQueryClient();
  const action=useChatCreationState(s=>s.action);
  const mutation=useMutation({mutationKey:['team-chat','creation'],retry:false,gcTime:0,mutationFn:async(intent:Intent)=>{
    lease(intent);const authorization=credentials.get(intent);if(!authorization)throw new ChatCreationError('unauthorized',false);
    let response;
    try {response=await client.post<unknown>({url:'/channels',body:intent.body,headers:{Authorization:authorization,'Accept-Language':intent.owner.language}});}catch {lease(intent);throw new ChatCreationError();}
    lease(intent);if(response.error||response.response?.status!==201)throw chatCreationProblem(response.error,response.response?.status??0);
    return readChatCreation(response.data,intent.owner,intent.body).channel;
  }});
  const mutateAsync=async(input:CreateChannelInput)=>{
    const owner=captureChatOwner();current(owner);
    if(ownAction(owner))throw new ChatCreationError('busy',false);
    const intent:Intent={owner,id:++sequence,phase:'pending',body:snapshotChatInput(input,owner)};
    authorize(intent);useChatCreationState.setState({action:intent});
    try {
      const result=await mutation.mutateAsync(intent);lease(intent);
      useChatCreationState.setState({action:null});
      void qc.invalidateQueries({queryKey:currentChatChannelsKey()});
      return result;
    }catch(error){
      if(owner.revision===sessionRevision()&&ownAction(owner)?.id===intent.id)useChatCreationState.setState({action:error instanceof ChatCreationError&&error.uncertain?{owner,id:intent.id,phase:'uncertain'}:null});
      throw error;
    }finally {credentials.delete(intent);}
  };
  const mutate=(input:CreateChannelInput)=>{void mutateAsync(input).catch(()=>{});};
  return {...mutation,mutate,mutateAsync,isPending:action?.phase==='pending',blocked:!!action};
}

// GET-only review never converts observations into a verified creation receipt.
// The explicit accept step merely enables another intentional action.
export async function reviewChatCreation():Promise<void> {
  const owner=captureChatOwner(),action=ownAction(owner);
  if(!action||!['uncertain','reviewed'].includes(action.phase))throw new ChatCreationError('busy',false);
  const request={};authorize(request);useChatCreationState.setState({action:{...action,phase:'reviewing',failed:false}});
  try {
    const rows:Channel[]=[];const ids=new Set<string>();
    for(let page=0;page<40;page++){
      lease(action);
      const response=await client.get<unknown>({url:'/channels',query:{limit:50,offset:page*50,include_archived:true},headers:{Authorization:credentials.get(request)!,'Accept-Language':owner.language}});
      lease(action);if(response.error||response.response?.status!==200)throw new ChatCreationError('review_failed',false);
      const batch=readChatPage(response.data,owner);if(batch.length>50||batch.some(row=>ids.has(row.id)))throw new ChatCreationError('review_failed',false);
      batch.forEach(row=>{ids.add(row.id);rows.push(row);});
      if(batch.length<50){useChatCreationState.setState({action:{owner,id:action.id,phase:'reviewed',review:rows}});return;}
    }
    throw new ChatCreationError('review_failed',false);
  }catch {
    current(owner);
    if(ownAction(owner)?.id===action.id)useChatCreationState.setState({action:{owner,id:action.id,phase:'uncertain',failed:true}});
    throw new ChatCreationError('review_failed',false);
  }finally {credentials.delete(request);}
}
export function acceptChatCreationReview() {
  const owner=captureChatOwner(),action=ownAction(owner);current(owner);
  if(action?.phase!=='reviewed')throw new ChatCreationError('busy',false);
  useChatCreationState.setState({action:null});
}
